import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Building2, Stethoscope, HeartPulse, ArrowRight, ShieldCheck, Activity, ClipboardList } from "lucide-react";
export default function Gateway() {
  const portals = [
    {
      id: "hospital",
      title: "Hospital Login",
      hi: "अस्पताल लॉगिन",
      desc: "Multi-department hospital admin & doctor management.",
      icon: Building2,
      accent: "from-sky-500 to-sky-600",
      ring: "ring-sky-200",
      testId: "hospital-login-btn",
      to: "/login/hospital",
    },
    {
      id: "clinic",
      title: "Clinic Login",
      hi: "क्लीनिक लॉगिन",
      desc: "Run a focused clinic with up-to-date patient flow.",
      icon: HeartPulse,
      accent: "from-emerald-500 to-emerald-600",
      ring: "ring-emerald-200",
      testId: "clinic-login-btn",
      to: "/login/clinic",
    },
    {
      id: "doctor",
      title: "Doctor Login",
      hi: "डॉक्टर लॉगिन",
      desc: "Sign in with your registered mobile, CID or MPIN.",
      icon: Stethoscope,
      accent: "from-slate-800 to-slate-900",
      ring: "ring-slate-200",
      testId: "doctor-login-btn",
      to: "/login/doctor",
    },
    {
      id: "receptionist",
      title: "Receptionist Login",
      hi: "रिसेप्शनिस्ट लॉगिन",
      desc: "Front-desk staff sign-in — verify gov cards, add patients, run queues.",
      icon: ClipboardList,
      accent: "from-indigo-500 to-indigo-600",
      ring: "ring-indigo-200",
      testId: "receptionist-login-btn",
      // Receptionist auth lives at its own route (not `/login/{id}`) — see App.js.
      to: "/receptionist/login",
    },
  ];  return (
    <div className="min-h-screen bg-slate-50 bg-grain" data-testid="gateway-page">
      {/* Top bar */}
      <header className="px-6 md:px-12 py-6 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-sky-500 to-emerald-500 flex items-center justify-center">
            <Activity className="w-5 h-5 text-white" />
          </div>
          <span className="font-heading text-lg font-bold tracking-tight text-slate-900">
            UpchaarQ
          </span>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/patient" className="hidden sm:inline-flex items-center gap-2 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 px-3 py-1.5 rounded-full hover:bg-emerald-100 transition-colors" data-testid="open-patient-app-btn">
            <HeartPulse className="w-3.5 h-3.5" /> Patient Portal
          </Link>
        </div>
      </header>

      <main className="px-6 md:px-12 pb-16">
        <div className="max-w-6xl mx-auto grid md:grid-cols-5 gap-10 md:gap-16 items-start mt-8 md:mt-16">
          {/* Left intro */}
          <div className="md:col-span-2">
            <p className="text-xs tracking-[0.3em] uppercase text-sky-600 font-semibold mb-4">
              UpchaarQ Doctor App
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 leading-[1.05]">
              One platform for{" "}
              <span className="brand-text">hospitals, clinics</span> and{" "}
              <span className="brand-text">doctors</span>.
            </h1>
            <p className="mt-6 text-base text-slate-600 leading-relaxed max-w-md">
              Manage live patient queues, daily earnings, doctor schedules and chat with your team — all from one focused workspace.
            </p>
          </div>

          {/* Right portal cards */}
          <div className="md:col-span-3 space-y-4">
            {portals.map((p) => {
              const Icon = p.icon;
              return (
                <Link
                  key={p.id}
                  to={p.to}
                  data-testid={p.testId}
                  className="group block"
                >
                  <div className={`medical-card p-6 md:p-7 flex items-center gap-6 hover:-translate-y-0.5 ring-1 ${p.ring}`}>
                    <div className={`w-14 h-14 shrink-0 rounded-xl bg-gradient-to-br ${p.accent} text-white flex items-center justify-center shadow-sm`}>
                      <Icon className="w-7 h-7" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-heading text-xl font-semibold text-slate-900 flex items-center gap-3">
                        {p.title}
                        <span className="text-sm font-normal text-slate-400">{p.hi}</span>
                      </h3>
                      <p className="text-sm text-slate-500 mt-1">{p.desc}</p>
                    </div>
                    <ArrowRight className="w-5 h-5 text-slate-400 group-hover:text-slate-700 group-hover:translate-x-1 transition-all" />
                  </div>
                </Link>
              );
            })}

            <div className="text-xs text-slate-400 pt-2">
              New hospital or clinic?{" "}
              <Link to="/login/hospital" className="text-sky-600 hover:underline" data-testid="register-hint-link">
                Create an admin account
              </Link>
              <span className="mx-2">·</span>
              <Link to="/super-admin/login" className="text-slate-500 hover:underline" data-testid="superadmin-link">
                Super admin
              </Link>
            </div>
            <Link to="/patient" className="sm:hidden inline-flex items-center gap-2 mt-3 text-sm font-semibold text-emerald-700" data-testid="open-patient-app-btn-mobile">
              <HeartPulse className="w-4 h-4" /> Patient Portal →
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}
